import { db } from "./db";

const tableByServiceName = {
  productos: db.productos,
  clientes: db.clientes,
  precios: db.precios,
  contactos: db.contactos,
  almacenes: db.almacenes,
  listaPrecios: db.lista_precios,
  condicionPagos: db.condicion_pagos,
  condiciones: db.condiciones,
  stock: db.stock,
  stockDRP: db.stock_drp,
  datoClientes: db.dato_clientes,
  deudaClientes: db.deuda_clientes,
  obras: db.obras,
  validezs: db.validezs,
  estados: db.estados,
  causas: db.causas,
  cierres: db.cierres,
  competencias: db.competencias,
};

const primaryKeyByServiceName = {
  almacenes: "__offlineId",
  clientes: "__offlineId",
  productos: "__offlineId",
  precios: "__offlineId",
  contactos: "__offlineId",
  listaPrecios: "__offlineId",
  condicionPagos: "__offlineId",
  condiciones: "__offlineId",
  stock: "__offlineId",
  stockDRP: "__offlineId",
  datoClientes: "__offlineId",
  deudaClientes: "__offlineId",
  obras: "__offlineId",
  validezs: "__offlineId",
  estados: "__offlineId",
  causas: "__offlineId",
  cierres: "__offlineId",
  competencias: "__offlineId",
};

function normalizeResponse(data) {
  if (typeof data === "string") {
    const trimmed = data.trim();

    if (trimmed.toUpperCase().includes("INSERT INTO")) {
      return parseMultipleInsertSql(trimmed);
    }

    return [];
  }

  if (Array.isArray(data)) return data;

  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.registros)) return data.registros;
  if (Array.isArray(data?.servicios)) return data.servicios;

  return [];
}

function parseMultipleInsertSql(sql) {
  const inserts = sql
    .split(/;\s*(?=INSERT\s+INTO)/i)
    .map((part) => part.trim())
    .filter(Boolean);

  return inserts.flatMap((insert) => {
    const normalizedInsert = insert.endsWith(";") ? insert : `${insert};`;
    return parseInsertSql(normalizedInsert);
  });
}

function parseInsertSql(sql) {
  const insertRegex =
    /INSERT\s+INTO\s+[`"]?([a-zA-Z0-9_]+)[`"]?\s*\(([^)]+)\)\s*VALUES\s*([\s\S]*);?$/i;
  const match = sql.match(insertRegex);

  if (!match) return [];

  const columns = match[2]
    .split(",")
    .map((column) => cleanSqlIdentifier(column));

  const valuesText = match[3].trim().replace(/;$/, "");
  const rows = splitSqlRows(valuesText);

  return rows.map((rowText, index) => {
    const values = splitSqlValues(rowText);
    const item = {};

    columns.forEach((column, columnIndex) => {
      item[column] = parseSqlValue(values[columnIndex]);
    });

    item.__offlineId = buildFallbackId(item, index);

    return item;
  });
}

function cleanSqlIdentifier(value) {
  return value.trim().replace(/[`"]/g, "");
}

function splitSqlRows(valuesText) {
  const rows = [];
  let current = "";
  let depth = 0;
  let inString = false;

  for (let i = 0; i < valuesText.length; i++) {
    const char = valuesText[i];
    const next = valuesText[i + 1];

    if (char === "'" && next === "'") {
      current += "''";
      i++;
      continue;
    }

    if (char === "'") {
      inString = !inString;
      current += char;
      continue;
    }

    if (!inString && char === "(") {
      depth++;
      if (depth === 1) continue;
    }

    if (!inString && char === ")") {
      depth--;
      if (depth === 0) {
        rows.push(current.trim());
        current = "";
        continue;
      }
    }

    if (!inString && depth === 0 && char === ",") {
      continue;
    }

    current += char;
  }

  return rows;
}

function splitSqlValues(rowText) {
  const values = [];
  let current = "";
  let inString = false;

  for (let i = 0; i < rowText.length; i++) {
    const char = rowText[i];
    const next = rowText[i + 1];

    if (char === "'" && next === "'") {
      current += "''";
      i++;
      continue;
    }

    if (char === "'") {
      inString = !inString;
      current += char;
      continue;
    }

    if (!inString && char === ",") {
      values.push(current.trim());
      current = "";
      continue;
    }

    current += char;
  }

  if (current.length > 0) {
    values.push(current.trim());
  }

  return values;
}

function parseSqlValue(value) {
  if (value === undefined) return null;

  const trimmed = value.trim();

  if (/^null$/i.test(trimmed)) return null;

  if (trimmed.startsWith("'") && trimmed.endsWith("'")) {
    return trimmed.slice(1, -1).replace(/''/g, "'");
  }

  if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
    return Number(trimmed);
  }

  return trimmed;
}

function buildFallbackId(item, index) {
  const values = Object.values(item)
    .map((value) => String(value ?? ""))
    .join("|");

  return `${values || "row"}-${index}`;
}

function removeDuplicates(registros, key) {
  if (!key) return registros;

  const map = new Map();

  registros.forEach((item) => {
    const value = item[key];

    if (value !== undefined && value !== null) {
      map.set(value, item);
    } else {
      map.set(item.__offlineId, item);
    }
  });

  return Array.from(map.values());
}

export async function saveSyncData(serviceName, responseData) {
  const table = tableByServiceName[serviceName];

  if (!table) {
    await db.sync_meta.put({
      recurso: serviceName,
      estado: "SIN_TABLA_LOCAL",
      ultimaSync: new Date().toISOString(),
      cantidadRegistros: 0,
      ultimoError: `No existe tabla local para ${serviceName}`,
    });

    return;
  }

  const primaryKey = primaryKeyByServiceName[serviceName];
  const registrosNormalizados = normalizeResponse(responseData);
  const registros = removeDuplicates(registrosNormalizados, primaryKey);

  await table.clear();

  if (registros.length > 0) {
    await table.bulkPut(registros);
  }

  await db.sync_meta.put({
    recurso: serviceName,
    estado: "OK",
    ultimaSync: new Date().toISOString(),
    cantidadRegistros: registros.length,
    ultimoError: null,
  });
}
